// src/perf/regression.ts
// Lightweight client-side regression detection for perf metrics.
// Designed to be efficient and safe for production use (can be disabled via env).
//
// Usage:
//   import { analyzeBatch, initRegression } from './perf/regression'
//   initRegression({ onAlert: (info) => { /* send to Sentry / console */ } })
//   analyzeBatch(metrics) // called when collector flushes a batch
//
// Behaviour:
// - Maintains per-metric sliding windows and persisted baselines (localStorage).
// - Triggers an alert when p95 or avg exceeds baseline by threshold (configurable).
// - Updates baseline with smoothing to avoid noisy flips.

import { type PerfMetric } from "./collector";

const LS_KEY = "perf-baselines-v1";

type Baseline = {
  avg: number;
  p95: number;
  count: number; // number of samples considered in baseline
  updatedAt: number;
};

type Options = {
  threshold?: number; // fraction, e.g. 0.5 = 50% increase triggers
  minSamples?: number; // minimum samples before evaluating regression
  smoothingAlpha?: number; // 0..1 smoothing for baseline updates
  maxBaselineCount?: number; // cap baseline count
  enabled?: boolean;
  onAlert?: (payload: {
    key: string;
    baseline: Baseline;
    current: { avg: number; p95: number; count: number };
    metricSamples: number[];
  }) => void;
};

const DEFAULTS: Required<Options> = {
  threshold: Number(import.meta.env.VITE_PERF_REGRESSION_THRESHOLD ?? 0.5),
  minSamples: Number(import.meta.env.VITE_PERF_REGRESSION_MIN_SAMPLES ?? 30),
  smoothingAlpha: Number(import.meta.env.VITE_PERF_REGRESSION_ALPHA ?? 0.2),
  maxBaselineCount: Number(
    import.meta.env.VITE_PERF_REGRESSION_MAX_COUNT ?? 1000,
  ),
  // Treat undefined as enabled by default; explicit "false" disables.
  enabled: (import.meta.env.VITE_PERF_REGRESSION_ENABLED ?? "true") === "true",
  onAlert: () => {},
};

let opts: Required<Options> = { ...DEFAULTS };

function loadBaselines(): Record<string, Baseline> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, Baseline>;
  } catch {
    return {};
  }
}

function saveBaselines(map: Record<string, Baseline>) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(map));
  } catch {
    // ignore storage errors
  }
}

function percentile(values: number[], p = 0.95) {
  if (!values.length) return 0;
  const sorted = values.slice().sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.floor(p * sorted.length));
  return sorted[idx];
}

function average(values: number[]) {
  if (!values.length) return 0;
  let s = 0;
  for (let v of values) s += v;
  return s / values.length;
}

export function initRegression(options?: Options) {
  opts = { ...DEFAULTS, ...(options || {}) };
}

export function resetBaselines() {
  saveBaselines({});
}

export function getBaselines(): Record<string, Baseline> {
  return loadBaselines();
}

/**
 * analyzeBatch - analyze a batch of perf metrics for regressions.
 * - metrics: array of PerfMetric (expected to include duration for timing types)
 * - returns: list of alert payloads (if any)
 */
export function analyzeBatch(metrics: PerfMetric[]) {
  if (!opts.enabled) return [];
  const baselines = loadBaselines();
  // aggregate samples by key
  const groups: Record<string, number[]> = {};

  for (const m of metrics) {
    const keyParts = [m.type, m.component ?? m.name ?? ""];
    const key = keyParts.join(":");
    const duration = m.duration ?? m.value;
    if (typeof duration !== "number" || Number.isNaN(duration)) continue;
    if (!groups[key]) groups[key] = [];
    groups[key].push(duration);
  }

  const alerts: any[] = [];

  for (const key of Object.keys(groups)) {
    const samples = groups[key];
    const curAvg = average(samples);
    const curP95 = percentile(samples, 0.95);
    const curCount = samples.length;

    const baseline = baselines[key];

    // If we don't have a baseline yet and we have enough samples, create one
    if (!baseline) {
      if (curCount >= opts.minSamples) {
        baselines[key] = {
          avg: curAvg,
          p95: curP95,
          count: Math.min(curCount, opts.maxBaselineCount),
          updatedAt: Date.now(),
        };
        // persist and continue
        saveBaselines(baselines);
      }
      continue;
    }

    // Only evaluate when we have enough new samples
    if (curCount < Math.max(3, Math.floor(opts.minSamples / 8))) {
      // not enough new data to evaluate
      // but we still incorporate the new samples into the baseline smoothing
      const alpha = opts.smoothingAlpha;
      baselines[key].avg = alpha * curAvg + (1 - alpha) * baselines[key].avg;
      baselines[key].p95 = alpha * curP95 + (1 - alpha) * baselines[key].p95;
      baselines[key].count = Math.min(
        opts.maxBaselineCount,
        baselines[key].count + curCount,
      );
      baselines[key].updatedAt = Date.now();
      saveBaselines(baselines);
      continue;
    }

    // Detect regression: if current p95 or avg is greater than baseline by threshold
    const threshold = 1 + opts.threshold;
    const p95Regressed = curP95 > baseline.p95 * threshold;
    const avgRegressed = curAvg > baseline.avg * threshold;

    if ((p95Regressed || avgRegressed) && baseline.count >= opts.minSamples) {
      const payload = {
        key,
        baseline: { ...baseline },
        current: { avg: curAvg, p95: curP95, count: curCount },
        metricSamples: samples.slice(0, 200),
      };
      try {
        opts.onAlert?.(payload);
      } catch {
        // swallow alert handler errors
      }
      alerts.push(payload);
      // Do not immediately overwrite baseline — instead smooth it so next checks are stable
      const alpha = opts.smoothingAlpha;
      baselines[key].avg = alpha * curAvg + (1 - alpha) * baselines[key].avg;
      baselines[key].p95 = alpha * curP95 + (1 - alpha) * baselines[key].p95;
      baselines[key].count = Math.min(
        opts.maxBaselineCount,
        baselines[key].count + curCount,
      );
      baselines[key].updatedAt = Date.now();
      saveBaselines(baselines);
      continue;
    }

    // No regression -> update baseline with smoothing
    {
      const alpha = opts.smoothingAlpha;
      baselines[key].avg = alpha * curAvg + (1 - alpha) * baselines[key].avg;
      baselines[key].p95 = alpha * curP95 + (1 - alpha) * baselines[key].p95;
      baselines[key].count = Math.min(
        opts.maxBaselineCount,
        baselines[key].count + curCount,
      );
      baselines[key].updatedAt = Date.now();
      saveBaselines(baselines);
    }
  } // end groups loop

  return alerts;
}
